class CartNotification extends HTMLElement {
  constructor() {
    super();

    this.notification = document.getElementById('cart-notification');
    this.header = document.querySelector('sticky-header');
    this.onBodyClick = this.handleBodyClick.bind(this);

    this.notification.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelectorAll('button[type="button"]').forEach((closeButton) =>
      closeButton.addEventListener('click', this.close.bind(this))
    );
  }

  open() {
    clearTimeout(this.toastTimer);
    this.notification.classList.add('animate', 'active');
    this.mobileToast = window.matchMedia('(max-width: 749px)').matches;
    this.notification.setAttribute('role', this.mobileToast ? 'status' : 'dialog');
    if (this.mobileToast) this.notification.removeAttribute('aria-modal');
    else this.notification.setAttribute('aria-modal', 'true');
    if (this.mobileToast) {
      this.focusTrapped = false;
      this.scheduleToastDismiss();
    } else {
      this.notification.focus();
      trapFocus(this.notification);
      this.focusTrapped = true;
    }

    document.body.addEventListener('click', this.onBodyClick);
  }

  close() {
    clearTimeout(this.toastTimer);
    this.notification.classList.remove('active');
    document.body.removeEventListener('click', this.onBodyClick);

    if (this.focusTrapped) removeTrapFocus(this.activeElement);
    this.focusTrapped = false;
  }

  scheduleToastDismiss(delay = 6000) {
    this.toastTimer = setTimeout(() => {
      if (!this.notification.classList.contains('active')) return;
      if (this.notification.contains(document.activeElement)) {
        this.scheduleToastDismiss(1500);
      } else {
        this.close();
      }
    }, delay);
  }

  // Show immediate feedback, then replace it with Shopify's authoritative badge.
  beginAdd(quantity) {
    quantity = Number(quantity);
    if (!Number.isSafeInteger(quantity) || quantity < 1) return null;
    this.pendingAdds ||= new Set();
    this.badgeRevision = (this.badgeRevision || 0) + 1;
    if (!this.pendingAdds.size) {
      this.confirmedBadge = document.getElementById('cart-icon-bubble').innerHTML;
      this.pendingBadgeCount = this.readBadgeCount();
      this.needsBadgeRead = Boolean(this.badgeReconcilePending);
    } else this.needsBadgeRead = true;
    const token = { quantity };
    this.pendingAdds.add(token);
    this.pendingBadgeCount += quantity;
    this.paintBadgeCount(this.pendingBadgeCount);
    return token;
  }

  readBadgeCount() {
    const bubble = document.querySelector('#cart-icon-bubble .cart-count-bubble');
    return Number(bubble?.dataset.cartCount || bubble?.querySelector('[aria-hidden="true"]')?.textContent || 0);
  }

  paintBadgeCount(count) {
    const cart = document.getElementById('cart-icon-bubble');
    let bubble = cart.querySelector('.cart-count-bubble');
    if (!bubble) {
      bubble = document.createElement('div');
      bubble.className = 'cart-count-bubble';
      bubble.setAttribute('aria-hidden', 'true');
      cart.append(bubble);
    }
    bubble.dataset.cartCount = count;
    bubble.textContent = count > 99 ? '99+' : String(count);
    cart.setAttribute('aria-busy', 'true');
  }

  settleAdd(token, response, uncertain = false) {
    if (!this.pendingAdds?.delete(token)) return;
    const html = response?.sections?.['cart-icon-bubble'];
    if (html) this.confirmedBadge = this.getSectionInnerHTML(html);
    else {
      this.pendingBadgeCount -= token.quantity;
      this.needsBadgeRead ||= uncertain || Boolean(response);
    }
    if (this.pendingAdds.size) {
      this.paintBadgeCount(this.pendingBadgeCount);
      return;
    }
    const cart = document.getElementById('cart-icon-bubble');
    cart.innerHTML = this.confirmedBadge;
    cart.removeAttribute('aria-busy');
    if (this.needsBadgeRead) this.reconcileBadge(this.badgeRevision);
  }

  async reconcileBadge(revision) {
    this.badgeReconcilePending = true;
    try {
      const response = await fetch(`${window.routes.cart_url}?section_id=cart-icon-bubble`, { headers: { Accept: 'text/html' } });
      if (!response.ok) return;
      const html = this.getSectionInnerHTML(await response.text());
      if (revision !== this.badgeRevision || this.pendingAdds.size) return;
      document.getElementById('cart-icon-bubble').innerHTML = html;
    } catch { /* Keep last confirmed count; never retry the add itself. */ }
    finally { if (revision === this.badgeRevision) this.badgeReconcilePending = false; }
  }

  renderContents(parsedState, badgeUpdated = false) {
    this.cartItemKey = parsedState.key;
    this.getSectionsToRender().forEach((section) => {
      if (section.id === 'cart-icon-bubble' && badgeUpdated) return;
      document.getElementById(section.id).innerHTML = this.getSectionInnerHTML(
        parsedState.sections[section.id],
        section.selector
      );
    });

    if (this.header) this.header.reveal();
    this.open();
    window.PrimoCartMotion?.play();
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-notification-product',
        selector: `[id="cart-notification-product-${this.cartItemKey}"]`,
      },
      {
        id: 'cart-notification-button',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  handleBodyClick(evt) {
    const target = evt.target;
    if (target !== this.notification && !target.closest('cart-notification')) {
      const disclosure = target.closest('details-disclosure, header-menu');
      this.activeElement = disclosure ? disclosure.querySelector('summary') : null;
      this.close();
    }
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-notification', CartNotification);
